/* eslint-disable max-lines-per-function */
import { useUmsatzAbsatzZiele } from '@/domain/controlling/hooks';
import { useReducer } from 'react';
import { useRouteParams } from '../useRouteParams';
import {
  formatNumber,
  getAbsatzProduktivitaetByMptOrStueck,
} from '../../../utils';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { ZieleEditForm, ZieleFormReducerAction } from '../types';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export function useZieleFormReducer() {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { searchParams } = useRouteParams();
  const { umsatzZiel, absatzZiel } = useUmsatzAbsatzZiele(
    standortId,
    searchParams.jahr,
    searchParams.monat,
  );
  const { tagebuchEinstellungen } = useTagebuchEinstellungen(standortId);

  const initalState: ZieleEditForm = {
    formValues: {
      absatzZiel: absatzZiel.wert
        ? formatNumber(absatzZiel.wert.toString(), true)
        : '',
      absatzZielproduktivitaet: getAbsatzProduktivitaetByMptOrStueck(
        absatzZiel.produktivitaet,
        tagebuchEinstellungen.absatzProduktivitaet,
        true,
      ),
      umsatzZiel: umsatzZiel.wert
        ? formatNumber(umsatzZiel.wert.amount.toString(), false)
        : '',
      umsatzZielproduktivitaet: umsatzZiel.produktivitaet
        ? formatNumber(umsatzZiel.produktivitaet.amount.toString(), false)
        : formatNumber(
            tagebuchEinstellungen.umsatzProduktivitaet.zielProduktivitaet.toString(),
            false,
          ),
    },
    hasChanges: false,
    isSubmitting: false,
  };

  // oxlint-disable-next-line complexity
  function zieleReducer(
    state: ZieleEditForm,
    action: ZieleFormReducerAction,
  ): ZieleEditForm {
    switch (action.type) {
      case 'updateAbsatzZiel': {
        return {
          formValues: {
            ...state.formValues,
            absatzZiel: action.values.absatzZiel,
          },
          hasChanges:
            action.values.absatzZiel !== initalState.formValues.absatzZiel,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateUmsatzZiel': {
        return {
          formValues: {
            ...state.formValues,
            umsatzZiel: action.values.umsatzZiel,
          },
          hasChanges:
            action.values.umsatzZiel !== initalState.formValues.umsatzZiel,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateUmsatzZielproduktivitaet': {
        return {
          formValues: {
            ...state.formValues,
            umsatzZielproduktivitaet: action.values.umsatzZielproduktivitaet,
          },
          hasChanges:
            action.values.umsatzZielproduktivitaet !==
            initalState.formValues.umsatzZielproduktivitaet,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateAbsatzZielproduktivitaet': {
        return {
          formValues: {
            ...state.formValues,
            absatzZielproduktivitaet: action.values.absatzZielproduktivitaet,
          },
          hasChanges:
            action.values.absatzZielproduktivitaet !==
            initalState.formValues.absatzZielproduktivitaet,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'submit': {
        return {
          ...state,
          isSubmitting: true,
        };
      }
      case 'updateDialogMessage': {
        return {
          ...state,
          dialogMessage: action.dialogMessage,
        };
      }
      case 'reset': {
        return initalState;
      }
    }
  }

  return useReducer(zieleReducer, initalState);
}
